import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, Avatar, Button, Tag, Spin, message } from "antd";
import axios from "axios";
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  DownloadOutlined,
  CheckCircleFilled,
  FileTextOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { TalentDetailsWrapper } from "./TalentDetails.style";
import { API_CONST } from "../../../../const";

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return `₹ ${Number(amount).toLocaleString('en-IN')}`;
};

const TalentDetails = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingOriginal, setDownloadingOriginal] = useState(false);
  const [downloadingBranded, setDownloadingBranded] = useState(false);
  
  const backPath = location.state?.from || "/admin/talent-profiles";
  const backLabel = location.state?.fromLabel || "Back to Talent Profiles";

  useEffect(() => {
    const fetchTalentProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_TALENT_PROFILE}/${talentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.profile) {
          const profile = response.data.profile;
          setTalent({
            ...profile,
            location: profile.location,
            experience: `${profile.experienceYears || 0} Years${profile.experienceMonths > 0 ? ` ${profile.experienceMonths} Months` : ''}`,
            noticePeriod: profile.notice || profile.noticePeriod || "Not specified",
            monthlyRate: formatCurrency(profile.rate || profile.monthlyRate),
            hourlyRate: formatCurrency(Math.round((profile.rate || profile.monthlyRate || 0) / 480)),
            marketRate: formatCurrency(Math.round((profile.rate || profile.monthlyRate || 0) * 1.1)),
            organization: profile.partnerOrganization,
            phone: profile.phone,
            skills: profile.skills || [],
            projects: profile.projects || [],
            totalRejected: profile.rejected,
          });
        }
      } catch (error) {
        console.error("Error fetching talent profile:", error);
        message.error("Failed to load talent profile");
      } finally {
        setLoading(false);
      }
    };

    if (talentId) {
      fetchTalentProfile();
    }
  }, [talentId]);

  if (loading) {
    return (
      <TalentDetailsWrapper>
        <div style={{ padding: "100px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
          <p style={{ marginTop: 16, color: "#666" }}>Loading talent profile...</p>
        </div>
      </TalentDetailsWrapper>
    );
  }

  if (!talent) {
    return (
      <TalentDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Talent not found</h2>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </TalentDetailsWrapper>
    );
  }

  return (
    <TalentDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
          className="back-button"
        >
          {backLabel}
        </Button>
      </div>

      <Card className="profile-card">
        <div className="profile-header">
          <div className="avatar-section">
            <Avatar size={100} className="talent-avatar">
              {talent.name.charAt(0)}
            </Avatar>
          </div>
          
          <div className="info-section">
            <h1>{talent.name}</h1>
            <p className="role">{talent.role}</p>
            <p className="organization">{talent.organization}</p>
            
            <div className="verification-badges">
              {talent.backgroundVerified && (
                <Tag 
                  icon={talent.backgroundVerified === "Yes" ? <CheckCircleFilled /> : null}
                  color={talent.backgroundVerified === "Yes" ? "success" : talent.backgroundVerified === "Pending" ? "warning" : "default"}
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  {talent.backgroundVerified === "Yes" ? "BGV: Verified" : 
                   talent.backgroundVerified === "Pending" ? "BGV: Pending" : "BGV: Not Verified"}
                </Tag>
              )}
              {talent.idVerificationStatus === "Verified" && (
                <Tag
                  icon={<CheckCircleFilled />}
                  color="success"
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  ID Verified
                </Tag>
              )}
              {talent.idVerificationStatus === "Failed" && (
                <Tag
                  color="error"
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  ID Failed
                </Tag>
              )}
            </div>
            
            <div className="meta-info">
              <div className="info-item">
                <EnvironmentOutlined />
                <span>{talent.location}</span>
              </div>
              <div className="info-item">
                <ClockCircleOutlined />
                <span>{talent.experience} Experience</span>
              </div>
              <div className="info-item">
                <CalendarOutlined />
                <span>{talent.noticePeriod} Notice</span>
              </div>
            </div>
          </div>

          <div className="action-section">
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              block
              onClick={() => navigate(`/admin/talent-profiles/documents/${talentId}`, { state: { from: 'details' } })}
            >
              View Documents
            </Button>
            <Button
              icon={<DownloadOutlined />}
              block
              loading={downloadingBranded}
              onClick={async () => {
                setDownloadingBranded(true);
                try {
                  const token = localStorage.getItem("authToken");
                  const response = await axios.get(
                    `${API_CONST.BRANDED_RESUME_BASE}/${talentId}/branded-resume`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (response.data.downloadUrl) window.open(response.data.downloadUrl, "_blank");
                  else message.error("Could not get resume download URL");
                } catch (e) {
                  message.error("Failed to download HireNXT resume");
                } finally {
                  setDownloadingBranded(false);
                }
              }}
            >
              Download HireNXT Resume
            </Button>
            <Button
              icon={<DownloadOutlined />}
              block
              loading={downloadingOriginal}
              disabled={!talent?.resumeUrl}
              onClick={async () => {
                if (!talent?.resumeUrl) { message.warning("No original resume uploaded"); return; }
                setDownloadingOriginal(true);
                try {
                  const token = localStorage.getItem("authToken");
                  const response = await axios.post(
                    API_CONST.GET_DOCUMENT_URL,
                    { objectPath: talent.resumeUrl },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (response.data.downloadUrl) window.open(response.data.downloadUrl, "_blank");
                } catch (e) {
                  message.error("Failed to download original resume");
                } finally {
                  setDownloadingOriginal(false);
                }
              }}
            >
              Download Original Resume
            </Button>
          </div>
        </div>

        <div className="details-row">
          <div className="detail-item">
            <h4>Hourly Rate</h4>
            <p className="highlight">{talent.hourlyRate}</p>
          </div>
          <div className="detail-item">
            <h4>Monthly Rate</h4>
            <p className="highlight">{talent.monthlyRate}</p>
          </div>
          <div className="detail-item">
            <h4>Market Rate</h4>
            <p>{talent.marketRate}</p>
          </div>
          <div className="detail-item">
            <h4>Availability</h4>
            <p>{talent.availability}</p>
          </div>
        </div>
      </Card>

      <Card className="metrics-card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Talent Metrics</h3>
          <div style={{ display: "flex", gap: 12 }}>
            <Tag 
              color={talent.status === "Active" ? "success" : "default"}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              {talent.status || "Active"}
            </Tag>
            <Tag 
              color={talent.performanceScore === "High" ? "green" : talent.performanceScore === "Average" ? "orange" : "red"}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              {talent.performanceScore || "Average"} Performance
            </Tag>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
          <div style={{ textAlign: "center", padding: 16, background: "#f8f9fd", borderRadius: 8 }}>
            <h2 style={{ margin: 0, color: "#014c75", fontSize: 24 }}>{talent.jobsApplied || 0}</h2>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>Jobs Applied</p>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#f6ffed", borderRadius: 8 }}>
            <h2 style={{ margin: 0, color: "#52c41a", fontSize: 24 }}>{talent.pastHired || 0}</h2>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>Past Hired</p>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#fff2f0", borderRadius: 8 }}>
            <h2 style={{ margin: 0, color: "#ff4d4f", fontSize: 24 }}>{talent.totalRejected || 0}</h2>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>Total Rejected</p>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#fff7e6", borderRadius: 8 }}>
            <h2 style={{ margin: 0, color: "#faad14", fontSize: 24 }}>
              {talent.jobsApplied ? ((talent.totalRejected || 0) / talent.jobsApplied * 100).toFixed(1) : 0}%
            </h2>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>Rejection Rate</p>
          </div>
          <div style={{ textAlign: "center", padding: 16, background: "#e6f7ff", borderRadius: 8 }}>
            <h2 style={{ margin: 0, color: "#1890ff", fontSize: 24 }}>{talent.avgContractDuration || "-"}</h2>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>Avg Contract Duration</p>
          </div>
        </div>
      </Card>

      <Card className="summary-card">
        <h3>Professional Summary</h3>
        <p>{talent.summary}</p>
      </Card>

      <Card className="skills-card">
        <h3>Primary Skills</h3>
        <div className="skills-grid">
          {talent.primarySkills && talent.primarySkills.length > 0 ? (
            talent.primarySkills.filter(Boolean).map((skill, index) => (
              <Tag key={index} color="cyan" style={{ fontSize: 14, padding: "6px 12px" }}>
                {skill}
              </Tag>
            ))
          ) : (
            <p style={{ color: "#999" }}>No primary skills added</p>
          )}
        </div>
      </Card>

      {talent.secondarySkills && talent.secondarySkills.length > 0 && (
        <Card className="skills-card">
          <h3>Secondary Skills</h3>
          <div className="skills-grid">
            {talent.secondarySkills.filter(Boolean).map((skill, index) => (
              <Tag key={index} color="blue" style={{ fontSize: 14, padding: "6px 12px" }}>
                {skill}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {talent.projects && talent.projects.length > 0 && (
        <Card className="projects-card">
          <h3>Project Experience</h3>
          {talent.projects.map((project, index) => (
            <div key={index} className="project-item">
              <h4>{project.title}</h4>
              <div className="project-meta">
                {project.client && <span><TeamOutlined /> {project.client}</span>}
                {project.duration && <span><CalendarOutlined /> {project.duration}</span>}
                {project.role && <span><DollarOutlined /> {project.role}</span>}
              </div>
              {project.description && <p>{project.description}</p>}
              {project.technologies && (
                <div className="tech-stack">
                  {(Array.isArray(project.technologies) 
                    ? project.technologies 
                    : project.technologies.split(',').map(t => t.trim()).filter(Boolean)
                  ).map((tech, idx) => (
                    <Tag key={idx} color="blue">
                      {tech}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}
    </TalentDetailsWrapper>
  );
};

export default TalentDetails;
